import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";
import { API_URLS } from "../../api/apiUrls";

// ─── Async Thunks ──────────────────────────────────────────────────────────

export const fetchAllGroups = createAsyncThunk(
  "group/fetchAllGroups",
  async (
    { pageNo = 1, pageSize = 10, search = "" } = {},
    { rejectWithValue },
  ) => {
    try {
      const url = API_URLS.GET_ALL_GROUPS(pageNo, pageSize, search);
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch groups";
      return rejectWithValue(message);
    }
  },
);
//------------------CREATE GROUP------------------
export const createGroups = createAsyncThunk(
  "group/createGroups",
  async ({ payload }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(API_URLS.CREATE_GROUP, payload);

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to create group",
      );
    }
  },
);
//-------------DELETE GROUP----------------------
export const deleteGroups = createAsyncThunk(
  "group/deleteGroups",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(API_URLS.DELETE_GROUP(id));
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to delete group",
      );
    }
  },
);
//-----------------GROUP STATUS UPDATE--------------
export const updateGroupsStatus = createAsyncThunk(
  "group/updateGroupsStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        API_URLS.UPDATE_GROUP_STATUS(id),
        { status },
      );
      return { id, status };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to update group status",
      );
    }
  },
);
//---------------FETCH GROUP CUSTOMERS-------------
export const fetchAllGroupCustomers = createAsyncThunk(
  "group/fetchAllGroupCustomers",
  async ({ groupId, pageNo = 1, pageSize = 10 }, { rejectWithValue }) => {
    try {
      const url = API_URLS.GET_ALL_CLIENT_UNDER_GROUP(
        groupId,
        pageNo,
        pageSize,
      );
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch customers",
      );
    }
  },
);

//--------------GET ALL CUSTOMER----------------------
export const getAllCustomers = createAsyncThunk(
  "group/getAllCustomers",
  async (
    { pageNo = 1, limit = 20, sort = "-createdAt", search = "" } = {},
    { rejectWithValue },
  ) => {
    try {
      const url = API_URLS.GET_ALL_CUSTOMER(pageNo, limit, sort, search);
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch customers";
      return rejectWithValue(message);
    }
  },
);

//-----------ASSIGN CLIENT TO GROUP---------------
export const assignToGroup = createAsyncThunk(
  "group/assignCustomerToGroup",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        API_URLS.ASSIGN_CLIENT_TO_GROUP,
        payload,
      );
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to assign customer";
      return rejectWithValue(message);
    }
  },
);
//-------DELETE CLIENT UNDER GROUP-------------
export const deleteGroupClient = createAsyncThunk(
  "group/deleteGroupsClient",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        API_URLS.DELETE_GROUP_CLIENT(id),
      );
      return id;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to delete group",
      );
    }
  },
);

export const fetchGroupById = createAsyncThunk(
  "group/fetchGroupById",
  async (id, { rejectWithValue }) => {
    try {
      const url = API_URLS.GET_GROUP_BY_ID(id);
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch group details";
      return rejectWithValue(message);
    }
  },
);

export const updateGroupDetails = createAsyncThunk(
  "group/updateGroupDetails",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const url = API_URLS.UPDATE_GROUP(id);
      const response = await axiosInstance.put(url, data);
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update group details";
      return rejectWithValue(message);
    }
  },
);

export const addGroupUser = createAsyncThunk(
  "group/addGroupUser",
  async ({ data, clientId }, { rejectWithValue }) => {
    try {
      const url = API_URLS.REGISTER_AND_ASSIGN_GROUP_USER;
      const response = await axiosInstance.post(url, data, {
        headers: { client: clientId },
      });
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to add group user";
      return rejectWithValue(message);
    }
  },
);

export const generateGroupUserOtp = createAsyncThunk(
  "group/generateGroupUserOtp",
  async ({ email }, { rejectWithValue }) => {
    try {
      const url = API_URLS.CUSTOMER_OTP_GENERATE;
      const response = await axiosInstance.post(url, { email });
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to generate OTP";
      return rejectWithValue(message);
    }
  },
);

export const fetchGroupUsers = createAsyncThunk(
  "group/fetchGroupUsers",
  async (
    {
      clientId,
      pageNo = 1,
      pageSize = 10,
      search = "",
      sortOrder = "-createdAt",
    },
    { rejectWithValue },
  ) => {
    try {
      const url = API_URLS.GET_ALL_USERS_BY_CLIENTID(clientId);
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch group users";
      return rejectWithValue(message);
    }
  },
);

export const uploadGroupDocument = createAsyncThunk(
  "group/uploadGroupDocument",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const url = API_URLS.UPLOAD_GROUP_DOCUMENT(id);
      const response = await axiosInstance.post(url, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to upload group document";
      return rejectWithValue(message);
    }
  },
);

export const fetchGroupDocuments = createAsyncThunk(
  "group/fetchGroupDocuments",
  async (id, { rejectWithValue }) => {
    try {
      const url = API_URLS.GET_GROUP_DOCUMENTS(id);
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch group documents";
      return rejectWithValue(message);
    }
  },
);

export const deleteGroupDocument = createAsyncThunk(
  "group/deleteGroupDocument",
  async ({ id, docId }, { rejectWithValue }) => {
    try {
      const url = API_URLS.DELETE_GROUP_DOCUMENT(id, docId);
      const response = await axiosInstance.delete(url);
      return { data: response.data, docId };
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete group document";
      return rejectWithValue(message);
    }
  },
);

export const manageGroupLicense = createAsyncThunk(
  "group/manageGroupLicense",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const url = API_URLS.MANAGE_GROUP_LICENSE(id);
      const response = await axiosInstance.patch(url, payload);
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to manage group license";
      return rejectWithValue(message);
    }
  },
);

export const grantViewAccess = createAsyncThunk(
  "group/grantViewAccess",
  async ({ groupId, clientId }, { rejectWithValue }) => {
    try {
      const url = API_URLS.GRANT_VIEW_ACCESS(groupId);
      const response = await axiosInstance.post(url, { clientId });
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to grant view access";
      return rejectWithValue(message);
    }
  },
);

export const revokeViewAccess = createAsyncThunk(
  "group/revokeViewAccess",
  async ({ groupId, clientId }, { rejectWithValue }) => {
    try {
      const url = API_URLS.REVOKE_VIEW_ACCESS(groupId);
      const response = await axiosInstance.delete(url, {
        data: { clientId },
      });
      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to revoke view access";
      return rejectWithValue(message);
    }
  },
);

// ─── Initial State ─────────────────────────────────────────────────────────

const initialState = {
  groups: [],
  allCustomers: [],
  addCustomers: [],
  currentGroup: null,
  groupUsers: [], // Store group users here
  groupDocuments: [], // Store group documents here
  groupUsersTotal: 0,
  totalRecords: 0,
  totalPages: 0,
  currentPage: 1,
  pageSize: 10,
  search: "",
  loading: false,
  loadingGroupCustomer: false,
  error: null,
  popup: { visible: false, type: "success", title: "", message: "" },
  groupForm: {
    groupName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    pincode: "",
    country: "",
    description: "",
  },
};

// ─── Slice ────────────────────────────────────────────────────────────────
//
const groupSlice = createSlice({
  name: "group",
  initialState,
  reducers: {
    setCurrentPage(state, action) {
      state.currentPage = action.payload;
    },
    setPageSize(state, action) {
      state.pageSize = action.payload;
    },
    setSearch(state, action) {
      state.search = action.payload;
      state.currentPage = 1; // reset to first page on new search
    },
    clearGroupError(state) {
      state.error = null;
    },
    setGroupFormField(state, action) {
      const { key, value } = action.payload;
      state.groupForm[key] = value;
    },

    resetGroupForm(state) {
      state.groupForm = initialState.groupForm;
    },
    showPopup(state, action) {
      state.popup = {
        visible: true,
        type: action.payload.type || "success",
        title: action.payload.title || "",
        message: action.payload.message || "",
      };
    },

    hidePopup(state) {
      state.popup.visible = false;
    },
  },

  extraReducers: (builder) => {
    builder
      // fetchAllGroups
      .addCase(fetchAllGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllGroups.fulfilled, (state, action) => {
        state.loading = false;
        const { data, metadata } = action.payload;
        state.groups = data ?? [];
        state.totalRecords = metadata?.total ?? 0;
        state.totalPages = metadata?.totalPages ?? 0;
        state.currentPage = metadata?.page ?? 1;
      })
      .addCase(fetchAllGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.groups = [];
      })
      // fetchGroupById
      .addCase(fetchGroupById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentGroup = action.payload?.data ?? null;
      })
      .addCase(fetchGroupById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentGroup = null;
      })
      // updateGroupDetails
      .addCase(updateGroupDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGroupDetails.fulfilled, (state, action) => {
        state.loading = false;
        // Optionally update currentGroup if backend returns updated details
        if (action.payload?.data) {
          state.currentGroup = action.payload.data;
        }
      })
      .addCase(updateGroupDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // addGroupUser
      .addCase(addGroupUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addGroupUser.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(addGroupUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // fetchGroupUsers
      .addCase(fetchGroupUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupUsers.fulfilled, (state, action) => {
        state.loading = false;
        const responseData = action.payload?.data;
        if (Array.isArray(responseData)) {
          state.groupUsers = responseData;
          state.groupUsersTotal = action.payload?.total ?? responseData.length;
        } else if (responseData && typeof responseData === "object") {
          // If it returns a group object, extract clients or users
          state.groupUsers = responseData.clients || responseData.users || [];
          state.groupUsersTotal = state.groupUsers.length;
        } else {
          state.groupUsers = [];
          state.groupUsersTotal = 0;
        }
      })
      .addCase(fetchGroupUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.groupUsers = [];
      })
      // fetchGroupDocuments
      .addCase(fetchGroupDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroupDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.groupDocuments = action.payload?.data ?? [];
      })
      .addCase(fetchGroupDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.groupDocuments = [];
      })
      // uploadGroupDocument
      .addCase(uploadGroupDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadGroupDocument.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(uploadGroupDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createGroups.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        state.popup = {
          visible: true,
          type: "success",
          title: "Group Created",
          message: "Group created successfully",
        };
      })
      .addCase(createGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.popup = {
          visible: true,
          type: "error",
          title: "Group Not Created",
          message: "Group Is Not Created",
        };
      })
      .addCase(deleteGroups.fulfilled, (state, action) => {
        const { updatedList } = action.payload;
        state.groups = updatedList?.data ?? [];
        state.totalRecords = updatedList?.metadata?.total ?? 0;
        state.totalPages = updatedList?.metadata?.totalPages ?? 0;
        state.currentPage = updatedList?.metadata?.page ?? 1;
      })
      .addCase(updateGroupsStatus.fulfilled, (state, action) => {
        const { id, status } = action.payload;
        const group = state.groups.find((g) => g._id === id);
        if (group) {
          group.status = status;
        }
        state.popup = {
          visible: true,
          type: "success",
          title: "Status Updated",
          message: `Group Is ${status}`,
        };
      })
      //-----------GET ALL CUSTOMERS-----------------
      .addCase(getAllCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllCustomers.fulfilled, (state, action) => {
        state.loading = false;
        const { data, metadata } = action.payload;
        state.addCustomers = data ?? [];
        state.totalRecords = metadata?.total ?? 0;
        state.totalPages = metadata?.totalPages ?? 0;
        state.currentPage = metadata?.currentPage ?? 1;
      })
      .addCase(getAllCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.addCustomers = [];
      })

      //----assign to customer----------------------
      .addCase(assignToGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })

      .addCase(assignToGroup.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(assignToGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      //----------fetch all group customer------------
      .addCase(fetchAllGroupCustomers.pending, (state) => {
        state.loadingGroupCustomer = true;
        state.error = null;
      })
      .addCase(fetchAllGroupCustomers.fulfilled, (state, action) => {
        state.loadingGroupCustomer = false;

        const { data, metadata } = action.payload;

        state.allCustomers = data ?? [];
        state.totalRecords = metadata?.total ?? 0;
        state.totalPages = metadata?.totalPages ?? 0;
        // state.currentPage = metadata?.currentPage ?? 1;
      })
      .addCase(fetchAllGroupCustomers.rejected, (state, action) => {
        state.loadingGroupCustomer = false;
        state.error = action.payload;
        state.allCustomers = [];
      })
      //--------DELETE GROUP-------------------
      .addCase(deleteGroupClient.fulfilled, (state, action) => {
        state.allCustomers = state.allCustomers.filter(
          (client) => client.clientId._id !== action.payload,
        );
      })
      // deleteGroupDocument
      .addCase(deleteGroupDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteGroupDocument.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.docId) {
          state.groupDocuments = state.groupDocuments.filter(
            (doc) => doc._id !== action.payload.docId,
          );
        }
      })
      .addCase(deleteGroupDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // grantViewAccess
      .addCase(grantViewAccess.fulfilled, (state, action) => {
        const { clientId } = action.meta.arg;
        const customer = state.allCustomers.find(
          (c) => c.clientId?._id === clientId,
        );
        const shouldUpdateLicense = !customer?.hasGroupViewAccess;

        if (customer) {
          customer.hasGroupViewAccess = true;
        }

        if (shouldUpdateLicense && state.currentGroup?.license?.group_view) {
          const groupViewLicense = state.currentGroup.license.group_view;
          groupViewLicense.used = (groupViewLicense.used || 0) + 1;
          groupViewLicense.unused = Math.max((groupViewLicense.unused || 0) - 1, 0);
        }
      })
      // revokeViewAccess
      .addCase(revokeViewAccess.fulfilled, (state, action) => {
        const { clientId } = action.meta.arg;
        const customer = state.allCustomers.find(
          (c) => c.clientId?._id === clientId,
        );
        const shouldUpdateLicense = Boolean(customer?.hasGroupViewAccess);

        if (customer) {
          customer.hasGroupViewAccess = false;
        }

        if (shouldUpdateLicense && state.currentGroup?.license?.group_view) {
          const groupViewLicense = state.currentGroup.license.group_view;
          groupViewLicense.used = Math.max((groupViewLicense.used || 0) - 1, 0);
          groupViewLicense.unused = (groupViewLicense.unused || 0) + 1;
        }
      });
  },
});

export const {
  setCurrentPage,
  setPageSize,
  setSearch,
  clearGroupError,
  setGroupFormField,
  resetGroupForm,
  showPopup,
  hidePopup,
} = groupSlice.actions;

export default groupSlice.reducer;
